import { useMutation } from 'react-query';
import { queryClient } from '../../client';
import { recordingKeys } from '../../recordings/keys';
import { Asset, EpisodeAsset } from '../titles/types/asset';
import { ProgramSource } from '../titles/types/sources';
import {
  cancelProgramRecording,
  cancelSeriesRecording,
  DeleteMode,
  deleteRecordings,
  deleteRecordingTask,
  recordProgram,
  recordSeries,
  rescheduleSeries,
  setDeleteMode,
  toggleDeleteMode,
} from './api';
import { recordingsQuery } from './queries';
import { LinkedEpisodeRecording, Recording, SeriesRecording } from './types/recording';

function invalidate() {
  queryClient.invalidateQueries(recordingsQuery());
  queryClient.invalidateQueries(recordingKeys.all);
}

interface RecordProgramMutationOptions {
  source: ProgramSource;
  deleteMode: DeleteMode;
}

interface RecordOptions {
  onSuccess?: () => void;
}

export function useRecordProgram(asset: Asset, options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (data: RecordProgramMutationOptions) => recordProgram(data.source, data.deleteMode),
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
    useErrorBoundary: true,
  });
}

interface RecordSeriesMutationOptions extends RecordProgramMutationOptions {
  rerunsAllowed: boolean;
}

export function useRecordSeries(asset: EpisodeAsset, options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (data: RecordSeriesMutationOptions) => {
      return recordSeries(data.source, {
        deleteMode: data.deleteMode,
        rerunsAllowed: data.rerunsAllowed,
        seasonNumber: asset.season.sequenceNumber,
        episodeNumber: asset.episode.sequenceNumber,
      });
    },
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
    useErrorBoundary: true,
  });
}

interface RescheduleSeriesMutationOptions {
  recording: SeriesRecording | LinkedEpisodeRecording;
  deleteMode: DeleteMode;
  rerunsAllowed: boolean;
}

export function useRescheduleSeries(asset: EpisodeAsset, options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (data: RescheduleSeriesMutationOptions) => {
      return rescheduleSeries(data.recording, {
        deleteMode: data.deleteMode,
        rerunsAllowed: data.rerunsAllowed,
        seasonNumber: asset.season.sequenceNumber,
        episodeNumber: asset.episode.sequenceNumber,
      });
    },
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
    useErrorBoundary: true,
  });
}

interface SetDeleteModeOptions {
  recording: Recording;
  deleteMode: DeleteMode;
}

export function useSetDeleteMode(options: RecordOptions = {}) {
  return useMutation({
    mutationFn: ({ recording, deleteMode }: SetDeleteModeOptions) => setDeleteMode(recording, deleteMode),
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}

export function useToggleDeleteMode(options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (recording: Recording) => toggleDeleteMode(recording),
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}

export function useCancelProgramRecording(options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (recordingId: string) => cancelProgramRecording(recordingId),
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}

export function useCancelSeriesRecording(
  options: RecordOptions = {},
  recording: SeriesRecording | LinkedEpisodeRecording,
) {
  return useMutation({
    mutationFn: async (taskId: string) => {
      const ongoingEpisodeRecording =
        'episodes' in recording ? recording.episodes.find((ep) => ep.status === 'ongoing') : undefined;

      return ongoingEpisodeRecording
        ? Promise.all([cancelProgramRecording(ongoingEpisodeRecording.id), cancelSeriesRecording(taskId)])
        : cancelSeriesRecording(taskId);
    },
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}

export function useDeleteRecordings(options: RecordOptions = {}) {
  return useMutation({
    mutationFn: (recordingIds: string[]) => deleteRecordings(recordingIds),
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}

interface DeleteRecordingTaskOptions {
  periodicTaskId: string;
  cancel?: boolean;
}

export function useDeleteSeriesRecording(options: RecordOptions = {}) {
  return useMutation({
    mutationFn: async ({ periodicTaskId, cancel }: DeleteRecordingTaskOptions) => {
      if (cancel) {
        await Promise.all([cancelSeriesRecording(periodicTaskId), deleteRecordingTask(periodicTaskId)]);
      }

      await deleteRecordingTask(periodicTaskId);
    },
    onSuccess: () => {
      invalidate();
      options.onSuccess?.();
    },
  });
}
